/* eslint-disable react-hooks/rules-of-hooks */
// src/components/page-content-v4/modules/PageForm.tsx
import React, { useState, useCallback } from 'react';
import FieldRenderer from './FieldRenderer';
import SEOFormSection from './SEOFormSection';
import CollapsibleSection from './CollapsibleSection';
import PageSettingsPanel from './PageSettingsPanel';
import ContentTabs, { TabType } from './ContentTabs';
import { Template, PageData, Field, AccountInfo } from './../types';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { AlertCircle, ChevronLeft, Maximize2, Minimize2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import LanguageSelector from './LanguageSelector';
import { useNavigate } from 'react-router-dom';

interface PageFormProps {
  templateId: string;
  template: Template;
  page: PageData;
  setPage: React.Dispatch<React.SetStateAction<PageData | null>>;
  onSave: (isDraft: boolean) => Promise<void>;
  isSeoEnabled: boolean;
  selectedLanguage: string;
  accountInfo: AccountInfo;
  isDraftEditing: boolean;
  handlePublishDraft: () => Promise<void>;
  reloadPageData: () => Promise<void>;
  discardDraft: () => Promise<void>;
}

const PageForm: React.FC<PageFormProps> = ({
  template,
  page,
  setPage,
  onSave,
  isSeoEnabled,
  selectedLanguage,
  accountInfo,
  isDraftEditing,
  handlePublishDraft,
  reloadPageData,
  discardDraft
}) => {
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const [activeTab, setActiveTab] = useState<TabType>('content');
  const [expandState, setExpandState] = useState<{ expanded: boolean; key: number } | undefined>(undefined);

  const findFieldByKey = useCallback((fields: Field[], key: string): Field | null => {
    for (const field of fields) {
      if (field.key === key) return field;
      if (field.children) {
        const found = findFieldByKey(field.children, key);
        if (found) return found;
      }
      if (field.templateFields) {
        const found = findFieldByKey(field.templateFields, key);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const validateField = useCallback((fieldKey: string, value: any): string | null => {
    const field = findFieldByKey(template?.fields || [], fieldKey);
    if (!field) return null;

    let error: string | null = null;

    if (field.settings.required) {
      if (!value || (Array.isArray(value) && value.length === 0) || value === '') {
        error = 'This field is required';
      }
    }

    if (field.type === 'url') {
      const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
      if (Array.isArray(value)) {
        if (value.some(url => !urlPattern.test(url))) {
          error = 'Invalid URL format';
        }
      } else if (value && !urlPattern.test(value)) {
        error = 'Invalid URL format';
      }
    }

    setFormErrors((prevErrors) => {
      if (error) {
        return { ...prevErrors, [fieldKey]: error };
      } else {
        const { [fieldKey]: removed, ...rest } = prevErrors;
        return rest;
      }
    });

    return error;
  }, [findFieldByKey, template.fields]);

  const handleContentChange = useCallback((fieldKey: string, value: any) => {
    setPage((prevPage) => {
      if (!prevPage) return null;
      return { ...prevPage, content: { ...prevPage.content, [fieldKey]: value } };
    });

    // Optionally validate on change
    if (formErrors[fieldKey]) {
      validateField(fieldKey, value);
    }
  }, [formErrors, setPage, validateField]);

  if (!page) {
    return <LoadingSpinner />;
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setPage((prevPage) => {
      if (!prevPage) return null;
      if (name.startsWith('seo.')) {
        const [, seoField] = name.split('.');
        return { ...prevPage, seo: { ...prevPage.seo, [seoField]: value } };
      }
      return { ...prevPage, [name]: value };
    });

    // Optionally validate on change
    if (formErrors[name]) {
      validateField(name, value);
    }
  };

  const validateAllFields = (): boolean => {
    const errors: Record<string, string> = {};

    const traverseFields = (fields: Field[], data: any) => {
      fields.forEach((field) => {
        const value = data[field.key];
        if (field.settings.required && (!value || (Array.isArray(value) && value.length === 0) || value === '')) {
          errors[field.key] = 'This field is required';
        }

        if (field.type === 'url') {
          const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
          if (Array.isArray(value)) {
            if (value.some(url => !urlPattern.test(url))) {
              errors[field.key] = 'Invalid URL format';
            }
          } else if (value && !urlPattern.test(value)) {
            errors[field.key] = 'Invalid URL format';
          }
        }

        if (field.children || field.templateFields) {
          const nestedData = field.children
            ? data[field.key]
            : field.templateFields
              ? Array.isArray(data[field.key]) ? data[field.key] : [data[field.key]]
              : null;

          if (nestedData) {
            if (Array.isArray(nestedData)) {
              nestedData.forEach((item: any) => traverseFields(field.children || [], item));
            } else {
              traverseFields(field.children || [], nestedData);
            }
          }
        }
      });
    };

    traverseFields(template?.fields || [], page.content);

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleValidate = (fieldKey: string, error: string | null) => {
    setTouchedFields((prev) => ({ ...prev, [fieldKey]: true }));
    if (error) {
      setFormErrors((prevErrors) => ({ ...prevErrors, [fieldKey]: error }));
    } else {
      setFormErrors((prevErrors) => {
        const { [fieldKey]: removed, ...rest } = prevErrors;
        return rest;
      });
    }
  };

  const handleSaveDraft = async () => {
    try {
      if(!page.title){
        toast.error('Title is required');
        return;
      }
      await onSave(true); // Save as draft
      toast.success('Page saved as draft!');
      await reloadPageData();
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft. Please try again.');
    }
  };

  const handleDiscardDraft = async () => {
    if (window.confirm('Are you sure you want to discard this draft? This action cannot be undone.')) {
      try {
        await discardDraft();
        toast.success('Draft discarded successfully!');
        reloadPageData(); // Reload data to reflect the discard
      } catch (error) {
        toast.error('Failed to discard draft. Please try again.');
      }
    }
  };

  const handlePublish = async () => {
    try {
      if(!page.title){
        toast.error('Title is required');
        return;
      }
      // First save the latest changes as a draft
      await onSave(true); // Save as draft
      toast.success('Draft saved! Preparing to publish...');
      
      // Now publish the draft
      await handlePublishDraft();
      toast.success('Page published successfully!');
      await reloadPageData();
    } catch (error) {
      console.error('Error publishing page:', error);
      toast.error('Failed to publish page. Please try again.');
    }
  };

  const handleSave = async () => {
    const isValid = validateAllFields();

    if (!isValid) {
      toast.error('Please correct the form errors before saving.');
      return;
    }

    try {
      if(!page.title){
        toast.error('Title is required');
        return;
      }
      await onSave(false); // Save as published
      toast.success('Page saved successfully!');
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error('Failed to save page. Please try again.');
    }
  };

  const navigate = useNavigate();

  const handleExpandAll = () => {
    setExpandState(prev => ({ expanded: true, key: (prev?.key || 0) + 1 }));
  };

  const handleCollapseAll = () => {
    setExpandState(prev => ({ expanded: false, key: (prev?.key || 0) + 1 }));
  };

  // Render top-level fields - each container/custom_template gets its own section
  const renderTopLevelFields = () => {
    const fields = template?.fields || [];
    
    return fields.map((field) => {
      const value = page.content[field.key];
      const error = formErrors[field.key] || null;
      const touched = touchedFields[field.key] || false;

      // For container/custom_template fields, render as collapsible sections
      if (field.type === 'container' || field.type === 'custom_template') {
        return (
          <CollapsibleSection
            key={field.id || field.key}
            title={field.label}
            defaultExpanded={false}
            forceState={expandState}
          >
            <FieldRenderer
              field={field}
              value={value}
              onChange={(key, val) => handleContentChange(key, val)}
              onValidate={(fieldKey, val) => {
                handleValidate(fieldKey, val);
                return null;
              }}
              error={error}
              touched={touched}
            />
          </CollapsibleSection>
        );
      }

      // For regular fields, just render them in a simple card
      return (
        <div key={field.id || field.key} className="bg-white border border-gray-200 rounded-lg p-5">
          <FieldRenderer
            field={field}
            value={value}
            onChange={(key, val) => handleContentChange(key, val)}
            onValidate={(fieldKey, val) => {
              handleValidate(fieldKey, val);
              return null;
            }}
            error={error}
            touched={touched}
          />
        </div>
      );
    });
  };

  return (
    <div className="min-h-screen bg-[#f0f5fa]">
      {/* Header - Same background as page */}
      <div className="sticky top-0 z-30 bg-[#f0f5fa]">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
                aria-label="Go back"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <h1 className="text-lg font-semibold text-gray-900">
                {page.pageId === 'new' ? 'Create Page' : 'Edit Page'}
              </h1>
            </div>
            <LanguageSelector
              selectedLanguage={selectedLanguage}
              locales={accountInfo?.locales || []}
              defaultLanguage={accountInfo?.defaultLanguage || 'en'}
            />
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Content Area */}
          <div className="flex-1 min-w-0">
            {/* Page Title Input */}
            <div className="bg-white border border-gray-100 rounded-xl p-4 mb-4 shadow-sm">
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                Page Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={page.title || ''}
                onChange={handleInputChange}
                onBlur={() => {
                  validateField('title', page.title);
                  setTouchedFields((prev) => ({ ...prev, title: true }));
                }}
                className={`w-full px-4 py-2.5 text-base border rounded-lg transition-colors focus:outline-none focus:ring-2
                  ${touchedFields.title && formErrors.title 
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-200 focus:ring-primary-500 focus:border-primary-500'
                  }`}
                placeholder="Enter page title"
              />
              {touchedFields.title && formErrors.title && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {formErrors.title}
                </p>
              )}
            </div>

            {/* Tabs */}
            <ContentTabs 
              activeTab={activeTab} 
              onTabChange={setActiveTab}
              isSeoEnabled={isSeoEnabled}
            />

            {/* Tab Content */}
            <div className="mt-4 space-y-3">
              {activeTab === 'content' && (
                <div className="space-y-3">
                  {/* Expand/Collapse All Controls */}
                  <div className="flex items-center justify-end gap-2 mb-2">
                    <button
                      type="button"
                      onClick={handleExpandAll}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 bg-white 
                        border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 
                        transition-colors duration-150 shadow-sm"
                    >
                      <Maximize2 className="h-3.5 w-3.5" />
                      Expand All
                    </button>
                    <button
                      type="button"
                      onClick={handleCollapseAll}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 bg-white 
                        border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 
                        transition-colors duration-150 shadow-sm"
                    >
                      <Minimize2 className="h-3.5 w-3.5" />
                      Collapse All
                    </button>
                  </div>
                  {renderTopLevelFields()}
                </div>
              )}

              {activeTab === 'seo' && isSeoEnabled && (
                <SEOFormSection page={page} onInputChange={handleInputChange} />
              )}

              {activeTab === 'settings' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Page Settings</h3>
                  <div className="space-y-4 text-sm">
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Template</span>
                      <span className="text-gray-900 font-medium">{template.templateName}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Page Key</span>
                      <span className="text-gray-900 font-mono text-xs">{page.pageKey || 'auto-generated'}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-gray-600">Content Language</span>
                      <span className="text-gray-900 font-medium uppercase">{page.language || 'en'}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar - Desktop Only */}
          <div className="hidden lg:block">
            <PageSettingsPanel
              page={page}
              isDraftEditing={isDraftEditing}
              onSaveDraft={handleSaveDraft}
              onPublish={handlePublish}
              onDiscardDraft={handleDiscardDraft}
            />
          </div>
        </div>

        {/* Mobile Actions - Sticky Bottom */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-20">
          <div className="flex gap-2">
            <button
              onClick={handleSaveDraft}
              className="flex-1 inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-lg 
                text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-colors"
            >
              Save Draft
            </button>
            {isDraftEditing ? (
              <button
                onClick={handlePublish}
                className="flex-1 inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-lg 
                  text-white bg-primary-500 hover:bg-primary-600 transition-colors"
              >
                Publish
              </button>
            ) : (
              <button
                onClick={handleSave}
                className="flex-1 inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium rounded-lg 
                  text-white bg-primary-500 hover:bg-primary-600 transition-colors"
              >
                Save & Publish
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageForm;